import {DataStatus, Search, Table, TableRow} from 'platform/components';
import {Box, Text, VStack} from 'platform/foundation';

import {useState} from 'react';
import {useSelector} from 'react-redux';

import {isEmptyArray} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {
  selectCarAudit,
  selectInspections,
  selectStructures,
} from '../../../store/carAudit/selectors';
import {selectUserSelectedLanguage} from '../../../store/user/selectors';
import {getTranslation} from '../utils/getTranslation';
import {Inspection} from './Inspection';

interface InspectionsProps extends TestIdProps {
  getRedirectRoute: (id: string, inspectionId: string) => string;
  openLinksInNewTab?: boolean;
}

export const Inspections = ({getRedirectRoute, openLinksInNewTab, ...rest}: InspectionsProps) => {
  const {loading} = useSelector(selectCarAudit);
  const inspections = useSelector(selectInspections);
  const structures = useSelector(selectStructures);
  const locale = useSelector(selectUserSelectedLanguage);
  const [searchValue, setSearchValue] = useState<string | null>(null);

  const filteredInspections =
    inspections.filter((inspection) => {
      const name = getTranslation(locale, structures?.[inspection.controlStructureId]?.name);
      return name.toLowerCase().includes(searchValue?.toLowerCase() ?? '');
    }) ?? [];

  return (
    <DataStatus
      isLoading={loading.getAudits}
      isEmpty={isEmptyArray(inspections)}
      data-testid={suffixTestId('inspections', rest)}
      emptyMessage={i18n.t('entity.condition.notifications.no.inspections')}
    >
      <Box backgroundColor="palettes.white.10.100" paddingTop={4}>
        <VStack spacing={2}>
          <Box maxWidth={60} paddingLeft={4} paddingRight={4}>
            <Search
              value={searchValue}
              onChange={setSearchValue}
              onClear={() => setSearchValue(null)}
            />
          </Box>
          <Table data-testid={suffixTestId('inspections', rest)}>
            <TableRow color="palettes.neutral.10.100" hoverColor="palettes.neutral.10.100">
              <Box paddingLeft={4} paddingVertical={2}>
                <Text size="small" align="left" alternative>
                  {i18n.t('entity.condition.labels.inspectionName')}
                </Text>
              </Box>
              <Box paddingVertical={2}>
                <Text size="small" align="left" alternative>
                  {i18n.t('general.labels.status')}
                </Text>
              </Box>
              <Box paddingVertical={2}>
                <Text size="small" align="left" alternative>
                  {i18n.t('general.labels.lastUpdated')}
                </Text>
              </Box>
              <Box paddingVertical={2}>
                <Text size="small" align="left" alternative>
                  {i18n.t('general.labels.digitalCertificate')}
                </Text>
              </Box>
              <Box paddingVertical={2}>
                <Text size="small" align="left" alternative>
                  {i18n.t('general.labels.author')}
                </Text>
              </Box>
              <Box paddingVertical={2}>
                <Text size="small" align="left" alternative>
                  {i18n.t('general.labels.assignee')}
                </Text>
              </Box>
              <Box paddingVertical={2}>
                <Text size="small" align="left" alternative>
                  {i18n.t('general.labels.rating')}
                </Text>
              </Box>
            </TableRow>
            {filteredInspections.map((auditData, index) => (
              <Inspection
                key={`${auditData.controlStructureId}-${index}`}
                data-testid={suffixTestId(`inspections-[${index}]`, rest)}
                getRedirectRoute={getRedirectRoute}
                openLinksInNewTab={openLinksInNewTab}
                auditData={auditData}
              />
            ))}
          </Table>
        </VStack>
      </Box>
    </DataStatus>
  );
};
